import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Modal, Spin } from "antd";
import { AlertTriangle, Archive, CheckCircle2, Eye, KeyRound, Loader, RefreshCw } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { CodeBlock, InlineValue, Note } from "../../../pages/enduser/ssh-proxy/sshProxyUi";
import RevealPasswordModal from "../../../pages/enduser/password-rotation/RevealPasswordModal";
import {
  decommissionEndpoint,
  decommissionPreview,
  endpointLifecycle,
  jobStatus,
  scanStatus,
  scanWaitingForSetup,
} from "../../../service/sshProxyService";

// Machine setup and account scans, shared by the agentless onboarding wizard
// and the endpoint's Gateway Access tab. (The Add endpoint wizard no longer
// issues a setup script — it sets the machine up through an existing admin
// account — so it only uses the scan view below.)
//
// Setup issues the key Authnull's authnull-svc account uses on the machine and
// returns a one-off script to run there as root. The gateway logs in with that
// key to scan the machine's accounts (and to install and revoke the per-session
// grant keys), so until the script has run every scan attempt fails its
// handshake — which is expected, not an error.

export const SCAN_POLL_MS = 5000;
const SCAN_TERMINAL = ["done", "failed", "expired"];

// One readable message for a failed reconcileSetup.
export function setupErrorText(err) {
  const status = err?.response?.status;
  const serverMsg = err?.response?.data?.error || "";
  if (status === 403) return "Administrator role required";
  if (status === 503) {
    return `${serverMsg ? serverMsg.charAt(0).toUpperCase() + serverMsg.slice(1) : "The vault is unavailable"}. The setup key is kept in the vault, so none can be issued until it is configured.`;
  }
  return serverMsg || "Couldn't create the setup script";
}

// The setup script and what it does. `result` is reconcileSetup's response;
// `error` is a message from setupErrorText.
export function SetupScriptBody({ loading, result, error, onRetry }) {
  if (loading) return <Spin size="small" />;
  if (error) {
    return (
      <div style={{ display: "grid", gap: 10, justifyItems: "start" }}>
        <Note tone="danger" icon={<AlertTriangle size={15} />}>
          {error}
        </Note>
        {onRetry && (
          <Button onClick={onRetry} icon={<RefreshCw size={13} style={{ verticalAlign: -2, marginRight: 6 }} />}>
            Retry
          </Button>
        )}
      </div>
    );
  }
  if (!result) return null;
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ fontSize: 12.5, color: AN_COLORS.ink2, fontFamily: AN_FONT, lineHeight: 1.5 }}>
        Run this once as root on the machine. It creates the <InlineValue>{result.account || "authnull-svc"}</InlineValue>{" "}
        account Authnull uses to install and revoke keys, and lets sshd read keys from{" "}
        <InlineValue>/etc/ssh/authnull/keys/</InlineValue>. Nothing else changes.
      </div>
      <CodeBlock copyText={result.script || ""} multiline>
        {result.script || ""}
      </CodeBlock>
      {result.keyFingerprint && (
        <div style={{ fontSize: 12, color: AN_COLORS.muted, fontFamily: AN_FONT }}>
          Key <span style={{ fontFamily: AN_MONO }}>{result.keyFingerprint}</span>
        </div>
      )}
      {result.scanJobId ? (
        <Note tone="info">
          The first account scan is queued. It starts 30 seconds from now and keeps retrying for up to a day, so run the
          script whenever suits you — the accounts arrive once it has.
        </Note>
      ) : (
        <Note tone="warn" icon={<AlertTriangle size={15} />}>
          This endpoint has no gateway, so no account scan was queued. Assign one, then scan its accounts.
        </Note>
      )}
    </div>
  );
}

// Where a scan is, from scanStatus's response: "waiting" (queued or running —
// including the normal "setup script not run yet" retries), "done",
// "failed" (failed or expired), "never", or "unknown" when it couldn't be read.
export function scanPhase(st) {
  if (!st) return null;
  if (scanWaitingForSetup(st) || st.status === "queued" || st.status === "claimed") return "waiting";
  if (st.status === "done") return "done";
  if (st.status === "failed" || st.status === "expired") return "failed";
  if (st.status === "never") return "never";
  return "unknown";
}

// The endpoint's latest scan. Read once on mount; while `polling` is on, read
// again every SCAN_POLL_MS until the scan is done, failed or expired.
// `onFinished(status)` fires when a scan this hook watched in progress ends.
export function useScanStatus(instanceId, { poll = false, onFinished } = {}) {
  const [status, setStatus] = useState(null); // null while loading
  const [polling, setPolling] = useState(poll);
  const alive = useRef(true);
  const prev = useRef(null);
  const finished = useRef(onFinished);
  finished.current = onFinished;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    if (!instanceId) return;
    scanStatus({
      instanceId,
      onSuccess: (st) => alive.current && setStatus(st),
      onFailure: () => alive.current && setStatus((s) => s || { status: "unknown" }),
    });
  }, [instanceId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!polling || !instanceId) return undefined;
    const t = setInterval(refresh, SCAN_POLL_MS);
    return () => clearInterval(t);
  }, [polling, instanceId, refresh]);

  useEffect(() => {
    if (!status) return;
    if (SCAN_TERMINAL.includes(status.status)) {
      setPolling(false);
      if (prev.current === "queued" || prev.current === "claimed") finished.current && finished.current(status);
    } else if (status.status === "queued" || status.status === "claimed") {
      // A scan already in progress when the page opened (queued by setup or
      // the daily schedule): follow it too, so its accounts appear on their own.
      setPolling(true);
    }
    prev.current = status.status;
  }, [status]);

  // After queuing a scan: show it as queued straight away and watch it.
  const watch = useCallback((jobId) => {
    prev.current = "queued";
    setStatus({ jobId, status: "queued", attempts: 0 });
    setPolling(true);
  }, []);

  return { status, polling, refresh, watch };
}

const fmtTime = (t) => {
  if (!t) return "";
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

const SOURCE_TAG = {
  gateway: { label: "Scanned", bg: AN_COLORS.primaryL, fg: AN_COLORS.primaryD },
  manual: { label: "Manual", bg: AN_COLORS.tint, fg: AN_COLORS.ink2 },
};

// Where an account came from: the scan ("gateway"), typed in ("manual"), or —
// for anything else — the agent.
export function SourceTag({ source }) {
  const t = SOURCE_TAG[source] || { label: "Agent", bg: AN_COLORS.tint, fg: AN_COLORS.muted };
  return (
    <span
      style={{
        display: "inline-block", fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase",
        padding: "1px 6px", borderRadius: 4, background: t.bg, color: t.fg, whiteSpace: "nowrap",
      }}
    >
      {t.label}
    </span>
  );
}

// The latest scan, in words. `accounts` ({ name, source }), when given, are
// listed under a finished scan. `neverText` says why no scan exists, which
// depends on where this is shown. The waiting and failed wording defaults to
// the setup-script flow; `waitingText`, `waitingHint` and `failedHint` replace
// it where the machine was set up another way (through an admin account).
export function ScanStatusBody({ status, accounts, neverText, waitingText, waitingHint, failedHint }) {
  const phase = scanPhase(status);
  const line = { display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontFamily: AN_FONT, color: AN_COLORS.ink2 };

  if (phase === null) return <Spin size="small" />;

  if (phase === "waiting") {
    return (
      <div style={{ display: "grid", gap: 6 }}>
        <div style={line}>
          <Spin size="small" />
          {waitingText || "Waiting for the setup script to be run on the machine…"}
        </div>
        <div style={{ fontSize: 12, color: AN_COLORS.muted, fontFamily: AN_FONT }}>
          {waitingHint || "This is normal until the script has run — the scan keeps retrying for up to a day."}
        </div>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div style={line}>
          <CheckCircle2 size={16} color={AN_COLORS.success} />
          <span style={{ fontWeight: 600, color: AN_COLORS.success }}>Ready</span>
          {status.completedAt && (
            <span style={{ fontSize: 12, color: AN_COLORS.muted }}>Scanned {fmtTime(status.completedAt)}</span>
          )}
        </div>
        {accounts &&
          (accounts.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {accounts.map((a) => (
                <span
                  key={`${a.source}:${a.name}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", border: `1px solid ${AN_COLORS.line}`, borderRadius: 7, background: AN_COLORS.bg }}
                >
                  <span style={{ fontFamily: AN_MONO, fontSize: 12, color: AN_COLORS.ink }}>{a.name}</span>
                  <SourceTag source={a.source} />
                </span>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: AN_COLORS.muted, fontFamily: AN_FONT }}>
              The scan found no login accounts on this machine.
            </div>
          ))}
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <Note tone="danger" icon={<AlertTriangle size={15} />}>
        <strong>The scan {status.status === "expired" ? "expired" : "failed"}</strong>
        {status.error ? (
          <>
            : <span style={{ fontFamily: AN_MONO, fontSize: 12 }}>{status.error}</span>
          </>
        ) : (
          "."
        )}{" "}
        {failedHint || (
          <>
            Re-run the setup script, then press <strong>Scan accounts</strong> on the endpoint.
          </>
        )}
      </Note>
    );
  }

  if (phase === "never") {
    return <div style={{ fontSize: 12.5, color: AN_COLORS.muted, fontFamily: AN_FONT }}>{neverText || "No scan has run on this endpoint yet."}</div>;
  }

  return <div style={{ fontSize: 12.5, color: AN_COLORS.muted, fontFamily: AN_FONT }}>Couldn't read the scan status.</div>;
}

// The banner a decommissioned (or decommissioning) endpoint's page opens with.
// Whether the machine was cleaned up, and when, comes from the clean-up job:
// "done" means the gateway removed Authnull's setup from it. An endpoint
// finished with force (the machine was unreachable) has no finished job, and
// its machine was not cleaned up — the banner must not say it was.
export function DecommissionBanner({ status, instanceId }) {
  const lifecycle = endpointLifecycle(status);
  const [job, setJob] = useState(undefined); // undefined while loading

  useEffect(() => {
    if (lifecycle !== "decommissioned" || !instanceId) return;
    jobStatus({
      instanceId,
      kind: "decommission",
      onSuccess: (st) => setJob(st || null),
      onFailure: () => setJob(null),
    });
  }, [lifecycle, instanceId]);

  if (!lifecycle) return null;
  if (lifecycle === "decommissioning") {
    return (
      <Note tone="warn" icon={<Loader size={15} />}>
        <b>Decommissioning:</b> the gateway is cleaning up the machine.
      </Note>
    );
  }
  if (job === undefined) return null;
  const cleaned = job?.status === "done";
  const date = cleaned && job.completedAt
    ? new Date(job.completedAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;
  return cleaned ? (
    <Note tone="info" icon={<Archive size={15} />}>
      <b>Decommissioned{date ? ` on ${date}` : ""}.</b> Access was removed and the machine was cleaned up. To use it
      again, run the enrol command on the machine.
    </Note>
  ) : (
    <Note tone="warn" icon={<Archive size={15} />}>
      <b>Decommissioned.</b> Access was removed, but the machine itself was not cleaned up — it couldn't be reached.
      If it still exists, remove the authnull-svc account and Authnull's files from it by hand. To use it again, run
      the enrol command on the machine.
    </Note>
  );
}

// ─── Decommission and recommission ───────────────────────────────────────────
// Shared by the Endpoints list's row menu and the Gateway Access tab.

export const DECOMMISSION_POLL_MS = 5000;
const DECOMMISSION_TERMINAL = ["done", "failed", "expired", "never"];

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function decommissionErrorText(err) {
  if (err?.response?.status === 403) return "Administrator role required";
  return capitalise(err?.response?.data?.error) || "Couldn't decommission the endpoint";
}

// { text, active } for a failed recommission. `active`: the endpoint is Active
// again anyway — only its setup script couldn't be issued — so the caller must
// still leave the decommissioned mode.
export function recommissionError(err) {
  const status = err?.response?.status;
  const serverMsg = err?.response?.data?.error || "";
  if (status === 403) return { text: "Administrator role required" };
  if (status === 409 && /still being decommissioned/i.test(serverMsg)) {
    return { text: "Still cleaning up — try again in about 30 seconds." };
  }
  if (status === 503) return { text: setupErrorText(err) };
  if (status === 500 && /active again/i.test(serverMsg)) {
    return { text: "The endpoint is active again, but its setup script couldn't be issued. Use Re-run setup.", active: true };
  }
  return { text: capitalise(serverMsg) || "Couldn't recommission the endpoint" };
}

// The recommission confirm, for modal.confirm. Recommissioning issues a new
// setup key, like Re-run setup, so it is asked first.
export const recommissionConfirm = (hostName) => ({
  title: `Recommission ${hostName || "this endpoint"}?`,
  content:
    "It becomes Active again with a new setup script to run on the machine as root — decommissioning removed the old setup. The first account scan is queued, and SSH policies that name this host issue their keys again.",
  okText: "Recommission",
  cancelText: "Cancel",
});

// Follows the clean-up job of every endpoint in `instanceIds` (those being
// decommissioned), reading jobStatus every DECOMMISSION_POLL_MS. Callers drop
// an id once its job has ended. `onFinished(instanceId, job)` fires once per
// job when it ends: "done" (the machine is clean and the endpoint
// Decommissioned), "failed" or "expired" (it stays Decommissioning — retry or
// finish with force), or "never" (no clean-up job at all). Returns the latest
// job per id.
export function useDecommissionJobs(instanceIds, onFinished) {
  const [jobs, setJobs] = useState({});
  const finished = useRef(onFinished);
  finished.current = onFinished;
  const reported = useRef(new Set());
  const key = (instanceIds || []).filter(Boolean).map(String).sort().join(",");

  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    const ids = key.split(",");
    const tick = () =>
      ids.forEach((id) =>
        jobStatus({
          instanceId: id,
          kind: "decommission",
          onSuccess: (st) => {
            if (!alive) return;
            setJobs((m) => ({ ...m, [id]: st }));
            if (!DECOMMISSION_TERMINAL.includes(st.status)) return;
            // A poll can land twice before the caller drops the id.
            const seen = `${id}:${st.jobId || st.status}`;
            if (reported.current.has(seen)) return;
            reported.current.add(seen);
            finished.current && finished.current(id, st);
          },
          onFailure: () => {},
        }),
      );
    tick();
    const t = setInterval(tick, DECOMMISSION_POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [key]);

  return jobs;
}

// Why a clean-up job ended without cleaning the machine, in words.
export function decommissionJobFailure(job) {
  if (!job || job.status === "never") return "No clean-up job was found for this endpoint.";
  const what = job.status === "expired" ? "The clean-up expired" : "The clean-up failed";
  return job.error ? `${what}: ${job.error}` : `${what}.`;
}

// "3 rotated passwords are kept in the vault — …" for a decommission answer
// whose passwordsKept is non-empty, else "". The callers' toasts end with it.
export function passwordsKeptText(res) {
  const n = Array.isArray(res?.passwordsKept) ? res.passwordsKept.length : 0;
  if (!n) return "";
  return `${n === 1 ? "1 rotated password is" : `${n} rotated passwords are`} kept in the vault — reveal ${n === 1 ? "it" : "them"} from the endpoint's page.`;
}

const plural = (n, one, many) => (n === 1 ? `1 ${one}` : `${n} ${many}`);
const listWords = (xs) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

// Asks before decommissioning, then makes the call. On open it reads
// decommissionPreview (grants that end, how the machine is cleaned up, which
// rotated passwords stay in the vault); if that fails it falls back to the
// generic text and still lets the admin go ahead. `defaultForce` pre-ticks
// Force (offered after a clean-up failed). `onDone(result)` receives the
// response — status "decommissioning" (follow the job) or "decommissioned",
// with passwordsKept (see passwordsKeptText). When the answer says the machine
// was NOT cleaned, the dialog stays open to show what to remove by hand.
export function DecommissionDialog({ open, instanceId, hostName, hasGateway = true, defaultForce = false, onClose, onDone }) {
  const [force, setForce] = useState(defaultForce);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notCleaned, setNotCleaned] = useState(null); // the decommission answer
  // loading | ready | error
  const [previewState, setPreviewState] = useState("loading");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [revealing, setRevealing] = useState(null); // account

  useEffect(() => {
    if (!open) return;
    setForce(defaultForce);
    setBusy(false);
    setError(null);
    setNotCleaned(null);
    setRevealing(null);
  }, [open, defaultForce]);

  useEffect(() => {
    if (!open || !instanceId) return undefined;
    let alive = true;
    setPreview(null);
    setPreviewError("");
    setPreviewState("loading");
    decommissionPreview({
      instanceId: Number(instanceId),
      onSuccess: (p) => {
        if (!alive) return;
        setPreview(p);
        setPreviewState("ready");
      },
      onFailure: (err) => {
        if (!alive) return;
        setPreviewError(
          err?.response?.status === 403
            ? "Administrator role required"
            : capitalise(err?.response?.data?.error) || "Couldn't check what decommissioning will do",
        );
        setPreviewState("error");
      },
    });
    return () => {
      alive = false;
    };
  }, [open, instanceId]);

  const submit = () => {
    setBusy(true);
    setError(null);
    decommissionEndpoint({
      instanceId,
      force: hasGateway ? force : false,
      onSuccess: (res) => {
        setBusy(false);
        onDone && onDone(res);
        if (res.status === "decommissioned" && res.machineCleaned === false) setNotCleaned(res);
        else onClose && onClose();
      },
      onFailure: (err) => {
        setBusy(false);
        setError(decommissionErrorText(err));
      },
    });
  };

  const text = { fontSize: 13, color: AN_COLORS.ink2, fontFamily: AN_FONT, lineHeight: 1.55 };
  const host = preview?.hostname || hostName || "this endpoint";

  if (notCleaned !== null) {
    const files = Array.isArray(notCleaned.cleanupFiles) ? notCleaned.cleanupFiles : [];
    // Paths go in the copyable block; anything else ("the authnull-svc account") is said in words.
    const paths = files.filter((f) => f.startsWith("/"));
    const others = files.filter((f) => !f.startsWith("/"));
    const kept = passwordsKeptText(notCleaned);
    return (
      <Modal
        open={open}
        title="Decommissioned — the machine was not cleaned up"
        onCancel={onClose}
        footer={<Button type="primary" onClick={onClose}>Done</Button>}
        width={560}
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div style={text}>Every key and grant on {host} has ended.</div>
          {files.length ? (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={text}>If the machine still exists, remove these by hand:</div>
              {paths.length > 0 && (
                <CodeBlock copyText={paths.join("\n")} multiline>
                  {paths.join("\n")}
                </CodeBlock>
              )}
              {others.length > 0 && <div style={text}>Also remove {listWords(others)}.</div>}
            </div>
          ) : (
            <Note tone="warn" icon={<AlertTriangle size={15} />}>
              {notCleaned.message || "Authnull's records are cleared, but the machine itself was not cleaned up."}
            </Note>
          )}
          {kept && <div style={text}>{kept.charAt(0).toUpperCase() + kept.slice(1)}</div>}
        </div>
      </Modal>
    );
  }

  const ready = previewState === "ready" && preview;
  const grants = Number(preview?.activeGrants) || 0;
  const kept = ready && Array.isArray(preview.keptPasswords) ? preview.keptPasswords : [];

  let cleanup;
  if (!ready) {
    cleanup = hasGateway
      ? "Then its gateway logs in one last time and removes Authnull's setup from the machine: the authnull-svc account, its keys and the sshd settings."
      : null;
  } else if (!preview.selfCleanup) {
    cleanup = "The machine can't be cleaned up automatically; you'll get a list of files to remove.";
  } else if (preview.accessMode === "admin") {
    cleanup = (
      <>
        Its gateway then logs in one last time and removes Authnull's keys and sshd settings. Your admin account (
        <span style={{ fontFamily: AN_MONO }}>{preview.adminAccount || "admin"}</span>) is left as it is.
      </>
    );
  } else {
    cleanup =
      "Its gateway then logs in one last time and removes Authnull's setup: the authnull-svc account, its keys and the sshd settings.";
  }

  const linkBtn = {
    border: 0, background: "none", padding: 0, cursor: "pointer", fontFamily: AN_MONO, fontSize: 12,
    color: "inherit", textDecoration: "underline", textUnderlineOffset: 2,
  };

  return (
    <Modal
      open={open}
      title={`Decommission ${host}?`}
      onCancel={busy ? undefined : onClose}
      maskClosable={!busy}
      width={560}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={busy}>
          Cancel
        </Button>,
        <Button key="ok" type="primary" danger loading={busy} disabled={previewState === "loading"} onClick={submit}>
          {force && hasGateway ? "Decommission with force" : "Decommission"}
        </Button>,
      ]}
    >
      <div style={{ display: "grid", gap: 14 }}>
        {previewState === "loading" ? (
          <div style={{ ...text, display: "flex", alignItems: "center", gap: 10, color: AN_COLORS.muted }}>
            <Spin size="small" /> Checking the endpoint…
          </div>
        ) : ready ? (
          <>
            <div style={text}>
              {grants === 0
                ? `No grants are open on ${host}, and no new session can be opened to it.`
                : `${plural(grants, "grant", "grants")} on ${host} end${grants === 1 ? "s" : ""} now, and no new session can be opened to it.`}
            </div>
            <div style={text}>
              {cleanup} Sessions, recordings and logs that name it are kept.
            </div>
          </>
        ) : (
          <>
            <div style={text}>
              Every key and grant on this endpoint ends now, and no new session can be opened to it. {cleanup}{" "}
              Sessions, recordings and logs that name it are kept.
            </div>
            <Note tone="info" icon={<AlertTriangle size={15} />}>
              {previewError}. You can still decommission it.
            </Note>
          </>
        )}
        {kept.length > 0 && (
          <Note tone="warn" icon={<KeyRound size={15} />}>
            <div>
              Authnull rotated the password{kept.length === 1 ? "" : "s"} of {plural(kept.length, "account", "accounts")} on
              this machine:{" "}
              {kept.map((k, i) => (
                <React.Fragment key={k.account}>
                  {i > 0 && ", "}
                  <span style={{ fontFamily: AN_MONO }}>{k.account}</span>
                  {k.unconfirmed && " (rotation in progress)"}
                </React.Fragment>
              ))}
              . The machine keeps {kept.length === 1 ? "that password" : "those passwords"} and only Authnull knows{" "}
              {kept.length === 1 ? "it" : "them"}. If the machine stays in use, reveal and save{" "}
              {kept.length === 1 ? "it" : "them"} first.
            </div>
            <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
              {kept.length === 1 ? (
                <Button size="small" icon={<Eye size={13} />} onClick={() => setRevealing(kept[0].account)} disabled={busy}>
                  Reveal password
                </Button>
              ) : (
                <>
                  <span style={{ fontWeight: 500 }}>Reveal passwords:</span>
                  {kept.map((k) => (
                    <button key={k.account} type="button" style={linkBtn} onClick={() => setRevealing(k.account)} disabled={busy}>
                      {k.account}
                    </button>
                  ))}
                </>
              )}
            </div>
          </Note>
        )}
        {hasGateway ? (
          <div style={{ display: "grid", gap: 4 }}>
            <Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} disabled={busy}>
              <span style={{ fontWeight: 500 }}>Force: the machine is gone</span>
            </Checkbox>
            <div style={{ fontSize: 12, color: AN_COLORS.muted, fontFamily: AN_FONT, paddingLeft: 24, lineHeight: 1.5 }}>
              Finish in Authnull only. Nothing on the machine is touched — if it still exists, you remove Authnull's
              files from it by hand.
            </div>
          </div>
        ) : (
          !ready && (
            <Note tone="warn" icon={<AlertTriangle size={15} />}>
              This endpoint has no gateway, so its machine can't be cleaned up from here. Authnull's records are cleared
              and you'll be shown what to remove by hand.
            </Note>
          )
        )}
        {error && (
          <Note tone="danger" icon={<AlertTriangle size={15} />}>
            {error}
          </Note>
        )}
      </div>
      <RevealPasswordModal
        open={!!revealing}
        endpointId={Number(instanceId)}
        account={revealing}
        hostname={preview?.hostname || hostName}
        onClose={() => setRevealing(null)}
      />
    </Modal>
  );
}
